import AddIcon from '@mui/icons-material/Add'
import { Button, MenuItem, Paper, Stack, TextField } from '@mui/material'
import { useRouter } from 'next/router'
import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useMutation, useQueryClient } from 'react-query'
import AvatarPreviewDialog from '../../features/avatar-preview/AvatarPreviewDialog'
import AmendOptionDialog from '../../features/option-grid/AmendOptionDialog'
import type { OptionAction } from '../../features/option-grid/OptionActionsMenu'
import OptionGrid from '../../features/option-grid/OptionGrid'
import ReorderConfirmDialog from '../../features/option-grid/ReorderConfirmDialog'
import RetireConfirmDialog from '../../features/option-grid/RetireConfirmDialog'
import OptionWizard from '../../features/option-wizard/OptionWizard'
import DraftSavedSnackbar from '../../features/publish/DraftSavedSnackbar'
import { useDraftDiff } from '../../features/publish/useDraftDiff'
import { publishKeys, useVersions } from '../../features/publish/useVersions'
import VersionChangesDialog from '../../features/publish/VersionChangesDialog'
import AddColourDialog from '../../features/section-editor/AddColourDialog'
import SectionEditorDrawer from '../../features/section-editor/SectionEditorDrawer'
import SectionRail from '../../features/section-rail/SectionRail'
import { useTierBands } from '../../features/tier-bands/useTierBands'
import { createAvatarBoClient } from '../../shared/AvatarBoClient'
import {
  selectionFromSlug,
  slugFromSelection,
} from '../../shared/catalogue-route'
import { pendingStagedVersion } from '../../shared/draft-state'
import { sortedOptionsFor } from '../../shared/layer-stack'
import {
  planReorder,
  sortedByTier,
  tierChangesForKeys,
  tierOrderBreaks,
} from '../../shared/reorder-plan'
import type {
  Direction,
  TierChange,
  TierOrderBreak,
} from '../../shared/reorder-plan'
import { formFromSection, toUpsertInput } from '../../shared/section-form'
import type {
  AvatarBoClient,
  BoOption,
  UpsertSectionInput,
} from '../../shared/types'
import DraftStateHeader from './DraftStateHeader'
import ErrorPane from './ErrorPane'
import { useAvatarCatalogue } from './useAvatarCatalogue'
import { useOptionMutations } from './useOptionMutations'
import { useOptionUpload } from './useOptionUpload'
import { useSectionMutation } from './useSectionMutation'

export interface CatalogueBrowserProps {
  apiBaseUrl: string
  authToken?: string
  client?: AvatarBoClient
}

export default function CatalogueBrowser({
  apiBaseUrl,
  authToken,
  client: injected,
}: CatalogueBrowserProps) {
  const { formatMessage } = useIntl()
  const router = useRouter()
  const fromUrl = selectionFromSlug(router?.query?.slug)
  const [setId, setSetId] = useState<string | undefined>(fromUrl.setId)
  const [sectionId, setSectionId] = useState<string | undefined>(
    fromUrl.sectionId,
  )
  const [showRetired, setShowRetired] = useState(false)
  const [editing, setEditing] = useState<'none' | 'new' | 'current'>('none')
  const [addingOption, setAddingOption] = useState(false)
  const [acting, setActing] = useState<
    { option: BoOption; action: OptionAction } | undefined
  >(undefined)
  const [showingChanges, setShowingChanges] = useState(false)
  // A colour is addressed by hex, not by a row, so it needs its own holder rather than `acting`.
  const [retiringSwatch, setRetiringSwatch] = useState<string | undefined>(
    undefined,
  )
  const [draftChanges, setDraftChanges] = useState(0)
  const [preview, setPreview] = useState<
    { sectionId: string; files?: File[] } | undefined
  >(undefined)
  const [addingColour, setAddingColour] = useState(false)
  const [swatchMove, setSwatchMove] = useState<
    | {
        swatches: string[]
        moved: string
        direction: Direction
        changes: TierChange[]
        breaks: TierOrderBreak[]
      }
    | undefined
  >(undefined)
  const [savedNotice, setSavedNotice] = useState(false)
  const qc = useQueryClient()

  // Every accepted change is a draft change: uploads and section saves count, not just the
  // option mutations that used to be the only thing the banner heard about.
  const noteDraftChange = () => {
    setDraftChanges((n) => n + 1)
    setSavedNotice(true)
    // A new edit outranks the last staging: the draft has moved on, so the button opens again
    // before the fresh comparison lands.
    stage.reset()
  }

  const remember = (next: { setId?: string; sectionId?: string }) => {
    if (!router?.replace) {
      return
    }
    router.replace(slugFromSelection(next), undefined, { shallow: true })
  }

  // A colour section has no upload, so adding one gets its own small dialog rather than the
  // whole section editor: adding is one field, editing the ladder is not.
  const saveSwatches = (swatches: string[]) => {
    if (!selected || !setId) {
      return
    }
    save.mutate(
      toUpsertInput(setId, { ...formFromSection(selected), swatches }),
    )
  }

  // A colour unlocks BY POSITION under a count ladder, so moving one is not cosmetic: it hands a
  // colour to a lower tier and pushes another up. Same warning a picture option gets.
  const moveSwatches = (
    swatches: string[],
    moved: string,
    direction: Direction,
  ) => {
    const before = selected?.swatches ?? []
    const rule = selected?.tierRule ?? null
    const changes = tierChangesForKeys(before, swatches, rule)
    // Two different warnings: a count ladder RE-TIERS on a move, and a per-colour ladder does not
    // but can be left reading backwards. Neither is obvious from the grid.
    const breaks = tierOrderBreaks(swatches, rule)
    if (changes.length === 0 && breaks.length === 0) {
      saveSwatches(swatches)

      return
    }
    setSwatchMove({ swatches, moved, direction, changes, breaks })
  }

  const addToSection = (kind?: string) => {
    if (kind === 'color') {
      clearServerErrors()
      setAddingColour(true)

      return
    }
    upload.reset()
    setAddingOption(true)
  }

  const client = useMemo(
    () =>
      injected ??
      createAvatarBoClient({ apiBaseUrl, authToken: () => authToken }),
    [injected, apiBaseUrl, authToken],
  )

  const { sets, catalogue } = useAvatarCatalogue({ client, setId })
  const ladder = useTierBands(client)
  const tierCount = ladder.query.data?.bands.length
  const versions = useVersions({ client })
  const knownVersions = versions.isError ? null : versions.data
  const live = versions.data?.find((v) => v.published)
  const liveDiff = useDraftDiff({
    client,
    setId,
    version: live?.n,
    catalogue: catalogue.data,
  })
  // The draft is measured against LIVE for the count, and against the version already waiting for
  // review to tell "something new to pass" from "already passed".
  const pending = pendingStagedVersion(knownVersions)
  const stagedDiff = useDraftDiff({
    client,
    setId,
    version: pending,
    catalogue: catalogue.data,
  })
  const stage = useMutation(() => client.stageVersion(), {
    onSuccess: () => {
      qc.invalidateQueries(publishKeys.versions('prod'))
      setSavedNotice(false)
    },
  })
  // The version the draft was last passed as, and whether it still matches. `stage.isSuccess`
  // carries the answer between a successful staging and the comparison that confirms it - the
  // window in which a second click would stage a duplicate. Any new edit clears it.
  const stagedVersion = pending ?? stage.data?.n
  const stagedMatchesDraft = stage.isSuccess
    ? true
    : stagedDiff.diff
      ? stagedDiff.diff.count === 0
      : undefined
  const upload = useOptionUpload({
    client,
    setId,
    sectionId,
    onRegistered: () => {
      setAddingOption(false)
      noteDraftChange()
    },
  })
  const options = useOptionMutations({
    client,
    setId,
    onChanged: () => {
      setActing(undefined)
      setRetiringSwatch(undefined)
      noteDraftChange()
    },
  })
  // The editor's colour list is re-sorted at save; pictures are separate rows, so their order is
  // realigned the same way HERE, right after the section row lands.
  const realignOptionOrder = (input: UpsertSectionInput) => {
    if (input.kind === 'color' || input.tierRule?.kind !== 'slugs') {
      return
    }
    const slugs = (
      sortedOptionsFor(catalogue.data?.options ?? [], input.id) as BoOption[]
    )
      .filter((o) => o.status === 'active')
      .map((o) => o.slug)
    const desired = sortedByTier(slugs, input.tierRule)
    const updates = desired
      .map((slug, index) => ({ slug, sortOrder: index }))
      .filter(({ slug, sortOrder }) => slugs[sortOrder] !== slug)
    if (updates.length) {
      options.reorder.mutate({ category: input.id, updates })
    }
  }

  const { save, serverErrors, clearServerErrors } = useSectionMutation({
    client,
    setId,
    onSaved: (input) => {
      setEditing('none')
      setAddingColour(false)
      setSectionId(input.id)
      realignOptionOrder(input)
      noteDraftChange()
    },
  })

  useEffect(() => {
    if (!setId && sets.data?.length) {
      setSetId(sets.data[0].id)
    }
  }, [sets.data, setId])

  const sections = catalogue.data?.sections ?? []

  useEffect(() => {
    if (sections.length && !sections.some((s) => s.id === sectionId)) {
      setSectionId(sections[0].id)
    }
  }, [sections, sectionId])

  useEffect(() => {
    setShowRetired(false)
  }, [sectionId])

  if (sets.isError) {
    return (
      <ErrorPane
        onRetry={() => sets.refetch()}
        message={formatMessage({ id: 'avatarStudio.error.setsFailed' })}
      />
    )
  }

  const selected = sections.find((s) => s.id === sectionId)
  const loading = catalogue.isLoading || sets.isLoading
  const optionMovePlan =
    acting && selected && isMove(acting.action)
      ? planReorder(
          catalogue.data?.options ?? [],
          selected.id,
          acting.option.slug,
          acting.action as Direction,
          selected.tierRule,
        )
      : { updates: [], tierChanges: [], orderBreaks: [] }

  return (
    <Stack spacing={2}>
      {/* ONE toolbar row, and everything that steers the page is in it: the collection and
          "New section" on the left - where reading starts and where the panes below hang off -
          and the draft's publish state on the right, using width that was empty.
          It replaces three stacked bands (banner, select, button) that between them ate a
          quarter of the page before a single option was visible. Every child is 40px and
          centred, so nothing sits 4px off its neighbour. Below `md` it becomes the old column,
          collection first, so a narrow screen keeps the same reading order. */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        sx={{ columnGap: 2, rowGap: 1.5 }}
        data-testid="catalogue-toolbar"
      >
        <Stack
          direction="row"
          alignItems="center"
          sx={{ columnGap: 1, flexShrink: 0 }}
        >
          <TextField
            select={true}
            size="small"
            label={formatMessage({ id: 'avatarStudio.collection.label' })}
            value={setId ?? ''}
            onChange={(e) => {
              setSetId(e.target.value)
              setSectionId(undefined)
              remember({ setId: e.target.value })
            }}
            // Fixed, not flexible: the label is a sentence ("OG Humans - 17 sections, 118
            // options"), so letting it grow with the row would push the publish state off screen.
            sx={{ width: { xs: '100%', md: 360 }, flexShrink: 0 }}
            disabled={sets.isLoading || !sets.data?.length}
          >
            {(sets.data ?? []).map((set) => (
              <MenuItem key={set.id} value={set.id}>
                {formatMessage(
                  { id: 'avatarStudio.collection.summary' },
                  {
                    name: set.name,
                    sections: set.sectionCount,
                    options: set.optionCount,
                  },
                )}
              </MenuItem>
            ))}
          </TextField>

          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={!setId}
            onClick={() => {
              clearServerErrors()
              setEditing('new')
            }}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {formatMessage({ id: 'avatarStudio.editor.newSection' })}
          </Button>
        </Stack>

        <DraftStateHeader
          // Computed, so a refresh and a second admin both see the same number.
          changes={liveDiff.diff?.count ?? draftChanges}
          onPassToReview={() => stage.mutate()}
          passing={stage.isLoading}
          staged={
            stagedVersion === undefined
              ? undefined
              : { version: stagedVersion, matchesDraft: stagedMatchesDraft }
          }
          // Only when there is a real diff behind the number: `draftChanges` is a local edit
          // counter and has no list to show, so the count stays plain text on that path.
          onShowChanges={
            liveDiff.diff ? () => setShowingChanges(true) : undefined
          }
          // NOT covered by a test, deliberately: on a first-load failure `data` is already
          // undefined, so this reads the same. It matters only if a REFETCH fails after a success,
          // where react-query keeps the stale `data` while `isError` is true and the banner would go
          // on naming an old version as live. Nothing here refetches versions yet, so the case is
          // unreachable - and this is what stops it appearing the day something does.
          versions={knownVersions}
        />
      </Stack>

      {catalogue.isError ? (
        <ErrorPane
          onRetry={() => catalogue.refetch()}
          message={formatMessage({ id: 'avatarStudio.error.catalogueFailed' })}
        />
      ) : (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems="stretch"
        >
          <Paper
            variant="outlined"
            sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0 }}
          >
            <SectionRail
              sections={sections}
              selectedId={sectionId}
              onSelect={(id) => {
                setSectionId(id)
                remember({ setId, sectionId: id })
              }}
              onEdit={(id) => {
                clearServerErrors()
                setSectionId(id)
                remember({ setId, sectionId: id })
                setEditing('current')
              }}
              loading={loading}
            />
          </Paper>
          <Paper variant="outlined" sx={{ flexGrow: 1, minWidth: 0 }}>
            <OptionGrid
              section={selected}
              options={catalogue.data?.options ?? []}
              loading={loading}
              showRetired={showRetired}
              onShowRetiredChange={setShowRetired}
              onAddOption={() => addToSection(selected?.kind)}
              onTryOn={
                catalogue.data
                  ? () => setPreview({ sectionId: sectionId as string })
                  : undefined
              }
              onReorderSwatches={selected && setId ? moveSwatches : undefined}
              onSwatchAction={
                selected && setId
                  ? (hex, action) => {
                      options.clearError()
                      // Restoring is reversible and needs no warning; removing asks first,
                      // exactly as it does for a picture.
                      if (action === 'restore') {
                        options.restoreSwatch.mutate({
                          setId,
                          category: selected.id,
                          hex,
                        })

                        return
                      }
                      setRetiringSwatch(hex)
                    }
                  : undefined
              }
              onOptionAction={(option, action) => {
                options.clearError()
                if (action === 'restore') {
                  options.restore.mutate({
                    setId: setId as string,
                    category: option.category,
                    slug: option.slug,
                  })

                  return
                }
                setActing({ option, action })
              }}
            />
          </Paper>
        </Stack>
      )}

      {acting && setId && (
        <RetireConfirmDialog
          open={acting.action === 'retire'}
          displayName={acting.option.displayName}
          busy={options.retire.isLoading}
          onCancel={() => setActing(undefined)}
          onConfirm={() =>
            options.retire.mutate({
              setId,
              category: acting.option.category,
              slug: acting.option.slug,
            })
          }
        />
      )}

      {/* The same dialog the publish screen opens, so "what changed" reads identically in both
          places. No `version`: this is the working draft, which has no number yet. */}
      {showingChanges && liveDiff.diff && (
        <VersionChangesDialog
          open={true}
          baseline={live?.n}
          baselineIsLive={true}
          changes={liveDiff.diff.changes}
          onClose={() => setShowingChanges(false)}
        />
      )}

      {retiringSwatch && setId && selected && (
        <RetireConfirmDialog
          open={true}
          displayName={`#${retiringSwatch}`}
          busy={options.retireSwatch.isLoading}
          onCancel={() => setRetiringSwatch(undefined)}
          onConfirm={() =>
            options.retireSwatch.mutate({
              setId,
              category: selected.id,
              hex: retiringSwatch,
            })
          }
        />
      )}

      {acting && setId && (
        <AmendOptionDialog
          open={acting.action === 'amend'}
          setId={setId}
          option={acting.option}
          busy={options.amend.isLoading}
          error={options.error}
          onCancel={() => setActing(undefined)}
          onSave={(input) => options.amend.mutate(input)}
        />
      )}

      {acting && setId && selected && isMove(acting.action) && (
        <ReorderConfirmDialog
          open={true}
          displayName={acting.option.displayName}
          direction={acting.action as Direction}
          tierChanges={optionMovePlan.tierChanges}
          orderBreaks={optionMovePlan.orderBreaks}
          busy={options.reorder.isLoading}
          onCancel={() => setActing(undefined)}
          onConfirm={() =>
            options.reorder.mutate({
              category: selected.id,
              updates: optionMovePlan.updates,
            })
          }
        />
      )}

      {setId && sectionId && (
        <OptionWizard
          open={addingOption}
          sectionId={sectionId}
          uploading={upload.uploading}
          progress={upload.progress}
          issues={upload.issues}
          onClose={() => setAddingOption(false)}
          onTryOn={(files) => setPreview({ sectionId, files })}
          onSubmit={(input) => void upload.start(input)}
        />
      )}

      <DraftSavedSnackbar
        open={savedNotice}
        publishHref={PUBLISH_HREF}
        onClose={() => setSavedNotice(false)}
      />

      {catalogue.data && (
        <AvatarPreviewDialog
          open={Boolean(preview)}
          catalogue={catalogue.data}
          changes={liveDiff.diff?.changes}
          initialSectionId={preview?.sectionId}
          tryOn={
            preview?.files?.length
              ? { files: preview.files, sectionId: preview.sectionId }
              : undefined
          }
          onAddOption={(id) => {
            setPreview(undefined)
            setSectionId(id)
            addToSection(sections.find((s) => s.id === id)?.kind)
          }}
          onClose={() => setPreview(undefined)}
        />
      )}

      {swatchMove && (
        <ReorderConfirmDialog
          open={true}
          displayName={swatchMove.moved}
          direction={swatchMove.direction}
          tierChanges={swatchMove.changes}
          orderBreaks={swatchMove.breaks}
          busy={save.isLoading}
          onCancel={() => setSwatchMove(undefined)}
          onConfirm={() => {
            saveSwatches(swatchMove.swatches)
            setSwatchMove(undefined)
          }}
        />
      )}

      {setId && (
        <AddColourDialog
          open={addingColour}
          section={selected}
          saving={save.isLoading}
          error={serverErrors.swatches ?? serverErrors.form}
          onClose={() => setAddingColour(false)}
          onAdd={(swatches) =>
            save.mutate(
              // The WHOLE row, or the upsert clears everything this dialog does not mention.
              // The new colour is unmentioned by the ladder, so it is T0 — sorted to where T0
              // lives rather than appended after the dearest tier.
              toUpsertInput(setId, {
                ...formFromSection(selected),
                swatches: sortedByTier(swatches, selected?.tierRule ?? null),
              }),
            )
          }
        />
      )}

      {setId && (
        <SectionEditorDrawer
          open={editing !== 'none'}
          tierCount={tierCount}
          setId={setId}
          section={editing === 'current' ? selected : undefined}
          options={catalogue.data?.options ?? []}
          saving={save.isLoading}
          serverErrors={serverErrors}
          onClose={() => setEditing('none')}
          onSave={(input) => save.mutate(input)}
        />
      )}
    </Stack>
  )
}

const PUBLISH_HREF = '/avatar-catalogue/publish'

const isMove = (action: OptionAction): boolean =>
  action === 'earlier' || action === 'later'
